import React, { useState, useEffect, useRef } from 'react';
import KanbanList from './KanbanList';
import KanbanCardModal from './KanbanCardModal';

const COLUMNS = ['Ideas', 'Bugs', 'Nice To Have', 'Must Have', 'In Progress', 'Done'];
const byOrder = (a, b) => (a.order || 0) - (b.order || 0);

export default function KanbanBoard({ isEditable }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Defaults to the typing-stars label since that's this board's primary
  // audience; falls back to "All" if that label has no cards yet.
  const [activeCategory, setActiveCategory] = useState('typing-stars');
  const [hasAppliedDefaultFilter, setHasAppliedDefaultFilter] = useState(false);
  const [openCardId, setOpenCardId] = useState(null);
  const [activeColumn, setActiveColumn] = useState(COLUMNS[0]);
  const boardScrollRef = useRef(null);
  const columnTabRefs = useRef({});

  const fetchItems = async (showLoading = false) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError('');
      const response = await fetch('/api/admin?action=kanban');
      const data = await response.json();

      if (data.success) {
        setItems(data.items || []);
      } else {
        setError(data.error || 'Failed to load items');
      }
    } catch (err) {
      console.error('Error fetching kanban items:', err);
      setError('Failed to load items');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchItems(true); // Only show loading on initial load
  }, []);

  // Once items have loaded, fall back to "All" if the default typing-stars
  // label turns out to have no cards (only run this once, on first load).
  useEffect(() => {
    if (hasAppliedDefaultFilter || loading) return;
    const hasTypingStars = items.some(item => (item.category || '').trim() === 'typing-stars');
    if (!hasTypingStars) {
      setActiveCategory(null);
    }
    setHasAppliedDefaultFilter(true);
  }, [loading, items, hasAppliedDefaultFilter]);

  // Keep the active column tab scrolled into view on the mobile tab strip
  useEffect(() => {
    const tab = columnTabRefs.current[activeColumn];
    if (tab) {
      tab.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [activeColumn]);

  const getAllCategories = () => {
    const categories = new Set();
    items.forEach(item => {
      if (item.category && item.category.trim()) {
        categories.add(item.category.trim());
      }
    });
    return Array.from(categories).sort();
  };

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
  });

  // Move a card to `targetColumn`, in front of `beforeId` (null = end of list).
  // `beforeId` comes from the visible (possibly filtered) list, so ordering is
  // resolved against the full column here.
  const handleCardDrop = async (itemId, targetColumn, beforeId) => {
    if (!isEditable) return;

    const item = items.find(i => i.id === itemId);
    if (!item) return;

    const sameColumn = item.column === targetColumn;
    const column = items.filter(i => i.column === targetColumn).sort(byOrder);
    const without = column.filter(i => i.id !== itemId);

    let insertIdx = without.length;
    if (beforeId) {
      const idx = without.findIndex(i => i.id === beforeId);
      if (idx !== -1) insertIdx = idx;
    }

    const currentIdx = column.findIndex(i => i.id === itemId);
    if (sameColumn && insertIdx === currentIdx) return; // dropped in place

    // Optimistic update
    let updatedItems;
    let newOrderValue = null;

    if (sameColumn) {
      const reordered = [...without];
      reordered.splice(insertIdx, 0, item);
      const orderById = new Map(reordered.map((i, idx) => [i.id, idx]));
      updatedItems = items.map(i =>
        orderById.has(i.id) ? { ...i, order: orderById.get(i.id) } : i
      );
    } else {
      // Insert between neighbours with a fractional order so the other cards
      // in the target column keep their stored order values.
      if (without.length === 0) {
        newOrderValue = 0;
      } else if (insertIdx === 0) {
        newOrderValue = (without[0].order || 0) - 1;
      } else if (insertIdx >= without.length) {
        newOrderValue = (without[without.length - 1].order || 0) + 1;
      } else {
        newOrderValue = ((without[insertIdx - 1].order || 0) + (without[insertIdx].order || 0)) / 2;
      }

      updatedItems = items.map(i => {
        if (i.id !== itemId) return i;
        return {
          ...i,
          column: targetColumn,
          order: newOrderValue,
          finishedAt: targetColumn === 'Done'
            ? (i.finishedAt || new Date().toISOString())
            : null
        };
      });
    }

    setItems(updatedItems);

    try {
      const requestBody = { action: 'kanban', id: itemId };
      if (sameColumn) {
        // The API expects the target index counted with the card still in its
        // old slot (it adjusts down itself when moving toward the end).
        requestBody.order = insertIdx > currentIdx ? insertIdx + 1 : insertIdx;
      } else {
        requestBody.column = targetColumn;
        requestBody.order = newOrderValue;
      }

      const response = await fetch('/api/admin?action=kanban', {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || 'Failed to update item');
      }

      // Silently refresh to sync with server (no loading spinner)
      await fetchItems(false);
    } catch (err) {
      console.error('Error updating item:', err);
      alert('Failed to move card. Please try again.');
      await fetchItems(false);
    }
  };

  const handleItemDelete = async (itemId) => {
    if (!isEditable) return;
    if (!window.confirm('Delete this card?')) {
      return;
    }

    try {
      const response = await fetch('/api/admin?action=kanban', {
        method: 'DELETE',
        headers: authHeaders(),
        body: JSON.stringify({ action: 'kanban', id: itemId })
      });

      const data = await response.json();
      if (data.success) {
        setItems(prevItems => prevItems.filter(item => item.id !== itemId));
        fetchItems(false).catch(err => console.error('Background refresh failed:', err));
      } else {
        throw new Error(data.error || 'Failed to delete item');
      }
    } catch (err) {
      console.error('Error deleting item:', err);
      await fetchItems(false);
      alert('Failed to delete card. Please try again.');
    }
  };

  const handleItemAdd = async (title, column) => {
    if (!isEditable) return;

    // Cards created while a label filter is active pick up that label,
    // otherwise they would vanish from view the moment they are added.
    const category = activeCategory || null;

    const response = await fetch('/api/admin?action=kanban', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        action: 'kanban',
        title,
        description: '',
        column,
        category
      })
    });

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to add item');
    }

    setItems(prevItems => [...prevItems, data.item]);
    fetchItems(false).catch(err => console.error('Background refresh failed:', err));
  };

  const handleItemEdit = async (itemId, title, description, category) => {
    if (!isEditable) return;

    setItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId
          ? { ...item, title, description, category: category || null }
          : item
      )
    );

    try {
      const response = await fetch('/api/admin?action=kanban', {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          action: 'kanban',
          id: itemId,
          title,
          description,
          category: category || null
        })
      });

      const data = await response.json();
      if (data.success) {
        fetchItems(false).catch(err => console.error('Background refresh failed:', err));
      } else {
        throw new Error(data.error || 'Failed to update item');
      }
    } catch (err) {
      console.error('Error updating item:', err);
      await fetchItems(false);
      throw err;
    }
  };

  // Export typing-stars items to CSV
  const exportTypingStarsToCSV = () => {
    const typingStarItems = items.filter(item => {
      const category = item.category || '';
      return category.toLowerCase() === 'typing-stars';
    });

    if (typingStarItems.length === 0) {
      alert('No typing-stars items found to export.');
      return;
    }

    typingStarItems.sort((a, b) => {
      const aColIndex = COLUMNS.indexOf(a.column || '');
      const bColIndex = COLUMNS.indexOf(b.column || '');

      if (aColIndex !== bColIndex) {
        return (aColIndex === -1 ? 999 : aColIndex) - (bColIndex === -1 ? 999 : bColIndex);
      }

      const aOrder = a.order !== undefined ? a.order : 999;
      const bOrder = b.order !== undefined ? b.order : 999;
      return aOrder - bOrder;
    });

    const escapeCsvField = (field) => {
      if (field === null || field === undefined) {
        return '';
      }

      const str = String(field);

      if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
        return `"${str.replace(/"/g, '""')}"`;
      }

      return str;
    };

    const formatDate = (dateString) => {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      } catch (e) {
        return dateString;
      }
    };

    const headers = [
      'ID',
      'Title',
      'Description',
      'Category',
      'Column',
      'Order',
      'Created At',
      'Finished At'
    ];

    const rows = typingStarItems.map(item => [
      escapeCsvField(item.id || ''),
      escapeCsvField(item.title || ''),
      escapeCsvField(item.description || ''),
      escapeCsvField(item.category || ''),
      escapeCsvField(item.column || ''),
      escapeCsvField(item.order !== undefined ? item.order : ''),
      escapeCsvField(formatDate(item.createdAt)),
      escapeCsvField(formatDate(item.finishedAt))
    ]);

    const csvLines = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ];

    const csvContent = csvLines.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const dateStr = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    const filename = `typing-stars-export-${dateStr}.csv`;

    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-neutral-700 border-t-neutral-200" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-md mx-auto text-center py-16">
        <p className="text-sm text-neutral-400 mb-3">{error}</p>
        <button
          onClick={() => fetchItems(true)}
          className="px-3 py-1.5 rounded-lg bg-neutral-800 hover:bg-neutral-700 text-neutral-200 text-sm transition-colors"
        >
          Try again
        </button>
      </div>
    );
  }

  const availableCategories = getAllCategories();
  const visibleItems = activeCategory
    ? items.filter(item => (item.category || '').trim() === activeCategory)
    : items;

  const itemsForColumn = (columnName) =>
    visibleItems.filter(item => item.column === columnName).sort(byOrder);

  const openCard = openCardId ? items.find(i => i.id === openCardId) : null;

  const chipClass = (active) =>
    `px-2.5 py-1 rounded-full text-xs transition-colors touch-manipulation whitespace-nowrap ${
      active
        ? 'bg-neutral-200 text-neutral-900 font-medium'
        : 'text-neutral-400 hover:text-neutral-200 hover:bg-neutral-800'
    }`;

  const columnCount = (columnName) => itemsForColumn(columnName).length;

  const scrollToColumn = (columnName) => {
    setActiveColumn(columnName);
    const board = boardScrollRef.current;
    if (!board) return;
    const columnEl = board.querySelector(`[data-column="${CSS.escape(columnName)}"]`);
    if (!columnEl) return;
    columnEl.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
  };

  const handleBoardScroll = () => {
    const board = boardScrollRef.current;
    if (!board) return;
    const columns = Array.from(board.querySelectorAll('[data-column]'));
    if (columns.length === 0) return;

    const scrollLeft = board.scrollLeft;
    let closest = columns[0];
    let closestDist = Infinity;
    columns.forEach((el) => {
      const dist = Math.abs(el.offsetLeft - scrollLeft);
      if (dist < closestDist) {
        closestDist = dist;
        closest = el;
      }
    });
    const name = closest.getAttribute('data-column');
    if (name) {
      setActiveColumn((prev) => (prev === name ? prev : name));
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Toolbar: label filter + actions */}
      <div className="shrink-0 flex items-center justify-between gap-2 mb-2 sm:mb-3">
        <div className="flex items-center gap-1 overflow-x-auto smooth-scroll min-w-0 pb-0.5 -mx-0.5 px-0.5">
          <button onClick={() => setActiveCategory(null)} className={chipClass(!activeCategory)}>
            All
          </button>
          {availableCategories.map(category => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={chipClass(activeCategory === category)}
            >
              {category}
            </button>
          ))}
        </div>
        {isEditable && (
          <button
            onClick={exportTypingStarsToCSV}
            className="shrink-0 px-2.5 py-1 rounded-lg text-xs text-neutral-500 hover:text-neutral-200 hover:bg-neutral-800 transition-colors touch-manipulation"
            title="Export typing-stars items to CSV"
          >
            Export CSV
          </button>
        )}
      </div>

      {/* Mobile column navigator — jump between lists without hunting via swipe */}
      <div className="md:hidden shrink-0 flex gap-1 overflow-x-auto smooth-scroll mb-2 pb-0.5 -mx-0.5 px-0.5">
        {COLUMNS.map(columnName => {
          const count = columnCount(columnName);
          const isActive = activeColumn === columnName;
          return (
            <button
              key={columnName}
              ref={(el) => { columnTabRefs.current[columnName] = el; }}
              type="button"
              onClick={() => scrollToColumn(columnName)}
              className={`shrink-0 px-2.5 py-1.5 rounded-lg text-xs touch-manipulation transition-colors ${
                isActive
                  ? 'bg-neutral-800 text-neutral-100 font-medium border border-neutral-600'
                  : 'text-neutral-400 border border-transparent hover:text-neutral-200 hover:bg-neutral-800/60'
              }`}
            >
              {columnName}
              <span className={`ml-1.5 tabular-nums ${isActive ? 'text-neutral-400' : 'text-neutral-600'}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Board — snap columns on mobile; free scroll on desktop */}
      <div
        ref={boardScrollRef}
        onScroll={handleBoardScroll}
        className="flex items-stretch gap-3 overflow-x-auto overflow-y-hidden flex-1 min-h-0 pb-3 snap-x snap-mandatory md:snap-none smooth-scroll"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {COLUMNS.map(columnName => (
          <KanbanList
            key={columnName}
            columnName={columnName}
            items={itemsForColumn(columnName)}
            isEditable={isEditable}
            onCardDrop={handleCardDrop}
            onCardClick={(item) => setOpenCardId(item.id)}
            onAddCard={(title, column) => handleItemAdd(title, column)}
          />
        ))}
        {/* Spacer so the last column can snap flush on mobile */}
        <div className="w-4 shrink-0 md:hidden" aria-hidden="true" />
      </div>

      {/* Card detail modal */}
      {openCard && (
        <KanbanCardModal
          item={openCard}
          isEditable={isEditable}
          columns={COLUMNS}
          availableCategories={availableCategories}
          onClose={() => setOpenCardId(null)}
          onSave={handleItemEdit}
          onMove={(itemId, column) => handleCardDrop(itemId, column, null)}
          onDelete={handleItemDelete}
        />
      )}
    </div>
  );
}
